'use client';

import { c as _c } from "react/compiler-runtime";
import { cx } from '@7f-ui';
import { BaseButton } from '@7f-ui/components/button/button';
import { CardSlider, CardSliderList, CardSliderListItem, CardSliderNavButtons } from '@7f-ui/components/card-slider/card-slider';
import { Chip } from '@7f-ui/components/chip/chip';
import { List, ListItem } from '@7f-ui/components/list/list';
import { MessageBox } from '@7f-ui/components/message-box/message-box';
import { Modal } from '@7f-ui/components/modal/modal';
import { Price } from '@7f-ui/components/price';
import { useCanScroll } from '@7f-ui/hooks/use-can-scroll';
import { useMediaState } from '@7f-ui/hooks/use-media-state';
import { AddedToCart, addedToCartItemsAtom, buildExperienceCartItem } from '@feat/feat-cart/added-to-cart';
import { useIsCartCurrencyMismatch } from '@feat/feat-cart/currency';
import { useCartItem } from '@feat/feat-cart/item';
import { useExperienceDuration } from '@feat/feat-experience/data';
import { trackClickCta, trackCtaSelection } from '@feat/feat-tracking/cta';
import { trackWebsiteMessage } from '@feat/feat-tracking/message';
import { useUrlState } from '@feat/feat-url-state/use-url-state';
import { CalendarBlank, CardsThree, CaretRight, Clock, User } from '@phosphor-icons/react';
import { KlarnaIcon } from '@shared/composites/payment-icons/klarna-icon';
import { PayPalIcon } from '@shared/composites/payment-icons/paypal-icon';
import { usePaymentPriorityAB } from '@shared/hooks/use-payment-priority-ab';
import { formatDateWithoutYear, formatDateWithWeekday } from '@utils/utils-format';
import { useAtom, useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import type { RefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppExperienceAvailableDates } from '../_hooks/use-app-experience-available-dates';
import { useExperienceOptions } from '../_hooks/use-app-experience-options';
import { useAppExperiencePersonsLabel } from '../_hooks/use-app-experience-persons-label';
import { useAppExperienceRecommendation } from '../_hooks/use-app-experience-recommendation';
import { useAppExperienceTimeslotsPreview } from '../_hooks/use-app-experience-timeslots-preview';
import { useCurrentPathSearch } from '../_hooks/use-current-path-search';
import { edpBookingModalOpenAtom, edpTimeslotSelectionAtom } from '../_lib/edp-booking-atoms';
import { getEdpTimeslotSelectionScope } from '../_lib/edp-timeslot-selection';
import { type EdpTimeslot, FilterType, getExperienceSelectionLabel, getSelectionFromFilter } from '../_lib/experience-booking-utils';
import type { ExperienceFilter } from '../_lib/experience-state-types';
import { AppDateModal } from './app-date-modal';
import { useExperienceFilterContext } from './app-experience-filter-provider';
import { AppExperienceTimeslotsSkeleton } from './app-experience-timeslots-skeleton';
import { AppMultiVariantModal } from './app-multi-variant-modal';
import { AppPaymentPriorityHint } from './app-payment-priority-hint';
import { AppPersonModal } from './app-person-modal';
import { AppVariantModal } from './app-variant-modal';
import { useEdpDateSelectionArm } from './edp-date-selection-arm';
const DateChip = dynamic(() => import('./date-chip').then(m => m.DateChip), {
  ssr: false
});
export function AppExperienceTimeslots() {
  const $ = _c(81);
  const {
    basePath,
    search
  } = useCurrentPathSearch();
  const {
    alternativeDates,
    dates,
    timeslots,
    prices,
    isLoading,
    isPlaceholderData,
    isError
  } = useAppExperienceTimeslotsPreview();
  const {
    filter,
    experience,
    dayPriceFailed
  } = useExperienceFilterContext();
  const {
    isVariant
  } = useEdpDateSelectionArm();
  const paymentPriority = usePaymentPriorityAB();
  const hasTrackedPaymentInfoRef = useRef(false);
  const setAddedToCartItemsAtom = useSetAtom(addedToCartItemsAtom);
  const {
    addExperience
  } = useCartItem();
  const {
    recommendation,
    loadRecommendation
  } = useAppExperienceRecommendation(experience.publicId);
  const [timeslotSelection, setTimeslotSelection] = useAtom(edpTimeslotSelectionAtom);
  let t0;
  if ($[0] !== experience.id || $[1] !== filter) {
    t0 = getEdpTimeslotSelectionScope(experience.id, filter);
    $[0] = experience.id;
    $[1] = filter;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const selectionScope = t0;
  const selectedEdpTimeslotId = timeslotSelection?.scope === selectionScope ? timeslotSelection.id : null;
  const setEdpBookingModalOpen = useSetAtom(edpBookingModalOpenAtom);
  const [selectedTimeslotId, setSelectedTimeslotId] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const termineHref = `${basePath}termine${search ? `?${search}` : ""}`;
  const cardSliderListRef = useRef(null);
  const {
    arePreliminary: dayPricesArePreliminary,
    isError: dayPricesFailed
  } = prices;
  const hasAlternativeDates = alternativeDates.length > 0;
  const showSkeleton = isLoading || isPlaceholderData;
  let t1;
  let t2;
  if ($[3] !== dates.length || $[4] !== experience.variants || $[5] !== filter || $[6] !== hasAlternativeDates || $[7] !== showSkeleton) {
    t1 = () => {
      if (showSkeleton) {
        return;
      }
      const filterSelection = getSelectionFromFilter(filter, experience.variants);
      const personsSelection = filterSelection.map(_temp).join("|");
      const unavailableMessage = hasAlternativeDates ? "no timeslots available - but others available" : "no timeslots available";
      const message = dates.length ? "success" : unavailableMessage;
      trackWebsiteMessage("experience", {
        action: "EDP timeslots",
        from: filter.from,
        to: filter.to,
        selection: personsSelection,
        message
      });
    };
    t2 = [dates.length, experience.variants, filter, hasAlternativeDates, showSkeleton];
    $[3] = dates.length;
    $[4] = experience.variants;
    $[5] = filter;
    $[6] = hasAlternativeDates;
    $[7] = showSkeleton;
    $[8] = t1;
    $[9] = t2;
  } else {
    t1 = $[8];
    t2 = $[9];
  }
  useEffect(t1, t2);
  if (isError || dayPricesFailed || dayPriceFailed) {
    let t3;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <section className="py-8"><MessageBox type="error">Termine oder Preise konnten gerade nicht geladen werden. Bitte lade die Seite neu.</MessageBox></section>;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    return t3;
  }
  if (showSkeleton || dayPricesArePreliminary) {
    let t3;
    if ($[11] !== paymentPriority.isVariant || $[12] !== timeslots.length) {
      t3 = <section className="py-8"><AppExperienceTimeslotsSkeleton cardCount={timeslots.length} isWalletFirst={paymentPriority.isVariant} /></section>;
      $[11] = paymentPriority.isVariant;
      $[12] = timeslots.length;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    return t3;
  }
  if (timeslots.length === 0) {
    return null;
  }
  let t3;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setSelectedTimeslotId(null);
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const closeModal = t3;
  let t4;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setActiveModal(null);
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const closeFilterModal = t4;
  let t5;
  if ($[16] !== experience.variants) {
    t5 = updatedFilter => {
      const selection_0 = getSelectionFromFilter(updatedFilter, experience.variants);
      trackCtaSelection("take over - persons", {
        selection: selection_0,
        type: "experience"
      });
      closeFilterModal();
    };
    $[16] = experience.variants;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const handlePaxSubmit = t5;
  let t6;
  if ($[18] !== addExperience || $[19] !== dayPricesArePreliminary || $[20] !== dayPricesFailed || $[21] !== experience || $[22] !== filter || $[23] !== loadRecommendation || $[24] !== setAddedToCartItemsAtom) {
    t6 = timeslot => new Promise(resolve => {
      if (!filter.selectedOptions || dayPricesArePreliminary || dayPricesFailed) {
        resolve(false);
        return;
      }
      addExperience({
        timeslotId: timeslot.id,
        experienceId: experience.id,
        selectedOptions: filter.selectedOptions
      }, {
        onSuccess: () => {
          setAddedToCartItemsAtom([buildExperienceCartItem(experience, {
            startDate: timeslot.date,
            startTimezone: timeslot.startTimezone
          })]);
          loadRecommendation();
          closeModal();
          resolve(true);
        },
        onError: () => resolve(false)
      });
    });
    $[18] = addExperience;
    $[19] = dayPricesArePreliminary;
    $[20] = dayPricesFailed;
    $[21] = experience;
    $[22] = filter;
    $[23] = loadRecommendation;
    $[24] = setAddedToCartItemsAtom;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const handleAddToCart = t6;
  let t7;
  if ($[26] !== recommendation) {
    t7 = <AddedToCart recommendation={recommendation} />;
    $[26] = recommendation;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== timeslots) {
    t8 = <Headline cardSliderListRef={cardSliderListRef} timeslots={timeslots} />;
    $[28] = timeslots;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t10;
  let t11;
  let t9;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setActiveModal(FilterType.date);
      trackClickCta("open calendar - on site", {
        type: "experience"
      });
    };
    t10 = () => {
      setActiveModal(FilterType.person);
      trackClickCta("open person selector - on site", {
        type: "experience"
      });
    };
    t11 = () => {
      setActiveModal(FilterType.variant);
      trackClickCta("open option - on site", {
        type: "experience"
      });
    };
    $[30] = t10;
    $[31] = t11;
    $[32] = t9;
  } else {
    t10 = $[30];
    t11 = $[31];
    t9 = $[32];
  }
  let t12;
  if ($[33] !== activeModal || $[34] !== handlePaxSubmit) {
    t12 = <FilterChips activeModal={activeModal} onClose={closeFilterModal} onDateClick={t9} onPersonClick={t10} onVariantClick={t11} onPaxSubmit={handlePaxSubmit} />;
    $[33] = activeModal;
    $[34] = handlePaxSubmit;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== dates.length) {
    t13 = dates.length === 0 && <MessageBox type="warning">An diesem Tag gibt es keine freien Termine. Wir können dir folgende Alternativen anbieten.</MessageBox>;
    $[36] = dates.length;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== experience.currency || $[39] !== isVariant || $[40] !== selectedEdpTimeslotId || $[41] !== selectionScope || $[42] !== setTimeslotSelection || $[43] !== timeslots) {
    let t15;
    if ($[45] !== experience.currency || $[46] !== isVariant || $[47] !== selectedEdpTimeslotId || $[48] !== selectionScope || $[49] !== setTimeslotSelection) {
      t15 = item => <CardSliderListItem key={item.id} className="relative grid-cols-3"><TimeslotItem timeslot={item} currency={experience.currency} isSelected={isVariant && selectedEdpTimeslotId === item.id} onClick={timeslot_0 => {
          if (isVariant) {
            setTimeslotSelection(prev => prev?.scope === selectionScope && prev.id === timeslot_0.id ? null : {
              id: timeslot_0.id,
              date: timeslot_0.date,
              scope: selectionScope
            });
          } else {
            setSelectedTimeslotId(timeslot_0.id);
          }
          trackClickCta("choose available date", {
            type: "experience",
            selection: `${timeslot_0.date} ${timeslot_0.startTime}`
          });
        }} /></CardSliderListItem>;
      $[45] = experience.currency;
      $[46] = isVariant;
      $[47] = selectedEdpTimeslotId;
      $[48] = selectionScope;
      $[49] = setTimeslotSelection;
      $[50] = t15;
    } else {
      t15 = $[50];
    }
    t14 = timeslots.map(t15);
    $[38] = experience.currency;
    $[39] = isVariant;
    $[40] = selectedEdpTimeslotId;
    $[41] = selectionScope;
    $[42] = setTimeslotSelection;
    $[43] = timeslots;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[51] !== isVariant || $[52] !== setEdpBookingModalOpen || $[53] !== termineHref) {
    t15 = <CardSliderListItem className="relative grid-cols-3">{isVariant ? <BaseButton className="flex h-full items-center gap-2 rounded-2xl border border-primary-green/15 bg-white p-6 font-body-copy hover:bg-hover-light active:bg-active-light" onClick={() => {
        trackClickCta("all timeslots", {
          type: "experience"
        });
        setEdpBookingModalOpen(true);
      }}><span className="font-body-small font-semibold">Verfügbarkeit prüfen</span><CaretRight height={16} width={16} /></BaseButton> : <Link href={termineHref} onClick={_temp2}><div className="flex h-full items-center gap-2 rounded-2xl border border-primary-green/15 bg-white p-6 font-body-copy hover:bg-hover-light active:bg-active-light"><span className="font-body-small font-semibold">Alle Termine anzeigen</span><CaretRight height={16} width={16} /></div></Link>}</CardSliderListItem>;
    $[51] = isVariant;
    $[52] = setEdpBookingModalOpen;
    $[53] = termineHref;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  let t16;
  if ($[55] !== t14 || $[56] !== t15) {
    t16 = <CardSliderList className="w-full pr-1" ref={cardSliderListRef} isSlidable={true}>{t14}{t15}</CardSliderList>;
    $[55] = t14;
    $[56] = t15;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  let t17;
  if ($[58] !== t12 || $[59] !== t13 || $[60] !== t16 || $[61] !== t8) {
    t17 = <div className="bleed-container xs:-mr-6 sm:mr-0"><CardSlider fullBleed={true}>{t8}{t12}{t13}{t16}</CardSlider></div>;
    $[58] = t12;
    $[59] = t13;
    $[60] = t16;
    $[61] = t8;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  let t18;
  if ($[63] !== paymentPriority.experimentId || $[64] !== paymentPriority.isVariant || $[65] !== paymentPriority.variationId) {
    t18 = paymentPriority.isVariant ? <AppPaymentPriorityHint hasTrackedInteractionRef={hasTrackedPaymentInfoRef} experimentId={paymentPriority.experimentId} variationId={paymentPriority.variationId} /> : <PaymentSection />;
    $[63] = paymentPriority.experimentId;
    $[64] = paymentPriority.isVariant;
    $[65] = paymentPriority.variationId;
    $[66] = t18;
  } else {
    t18 = $[66];
  }
  let t19;
  if ($[67] !== t17 || $[68] !== t18 || $[69] !== t7) {
    t19 = <div className="flex flex-col gap-6">{t7}{t17}{t18}</div>;
    $[67] = t17;
    $[68] = t18;
    $[69] = t7;
    $[70] = t19;
  } else {
    t19 = $[70];
  }
  let t20;
  if ($[71] !== selectedTimeslotId || $[72] !== timeslots) {
    t20 = timeslots.find(slot => slot.id === selectedTimeslotId) ?? null;
    $[71] = selectedTimeslotId;
    $[72] = timeslots;
    $[73] = t20;
  } else {
    t20 = $[73];
  }
  const t21 = dayPricesArePreliminary || dayPricesFailed;
  let t22;
  if ($[74] !== handleAddToCart || $[75] !== t20 || $[76] !== t21) {
    t22 = <AddToCartModal timeslot={t20} pricesUnavailable={t21} onSubmit={handleAddToCart} onClose={closeModal} />;
    $[74] = handleAddToCart;
    $[75] = t20;
    $[76] = t21;
    $[77] = t22;
  } else {
    t22 = $[77];
  }
  let t23;
  if ($[78] !== t19 || $[79] !== t22) {
    t23 = <section className="py-8">{t19}{t22}</section>;
    $[78] = t19;
    $[79] = t22;
    $[80] = t23;
  } else {
    t23 = $[80];
  }
  return t23;
}
function _temp2() {
  return trackClickCta("all timeslots", {
    type: "experience"
  });
}
function _temp(selection) {
  return `${selection.amount}x${selection.name}`;
}
type TimeslotItemProps = {
  timeslot: EdpTimeslot;
  currency: string;
  isSelected?: boolean;
  onClick: (timeslot: EdpTimeslot) => void;
};
function TimeslotItem(t0) {
  const $ = _c(27);
  const {
    timeslot,
    currency,
    isSelected,
    onClick
  } = t0;
  const duration = useExperienceDuration(timeslot.duration);
  const {
    filter,
    baseVariants
  } = useExperienceFilterContext();
  let t1;
  if ($[0] !== baseVariants || $[1] !== filter) {
    t1 = getExperienceSelectionLabel(filter, baseVariants);
    $[0] = baseVariants;
    $[1] = filter;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const selectionLabel = t1;
  let t2;
  if ($[3] !== onClick || $[4] !== timeslot) {
    t2 = () => onClick(timeslot);
    $[3] = onClick;
    $[4] = timeslot;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = `ExperienceTimeslots.slot.${timeslot.id}`;
  const t4 = isSelected ? "border-2 border-primary" : "border border-primary-green/15";
  let t5;
  if ($[6] !== t4) {
    t5 = cx("flex w-58 flex-col items-start gap-2 rounded-2xl bg-white p-4 text-left font-body-copy hover:bg-hover-light active:bg-active-light", t4);
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== timeslot.date) {
    t6 = formatDateWithWeekday(timeslot.date);
    $[8] = timeslot.date;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <p className="font-body-copy font-semibold">{t6}</p>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = `${timeslot.startTime} Uhr · ${duration}`;
  let t9;
  if ($[12] !== t8) {
    t9 = <p className="font-body-small">{t8}</p>;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== selectionLabel) {
    t10 = <p className="font-body-small">{selectionLabel}</p>;
    $[14] = selectionLabel;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== currency || $[17] !== timeslot.price) {
    t11 = <p className="font-body-small text-secondary-text"><Price className="font-body-copy font-semibold" amount={timeslot.price} currency={currency} />{" inkl. MwSt."}</p>;
    $[16] = currency;
    $[17] = timeslot.price;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10 || $[20] !== t11 || $[21] !== t2 || $[22] !== t3 || $[23] !== t5 || $[24] !== t7 || $[25] !== t9) {
    t12 = <BaseButton onClick={t2} data-testid={t3} className={t5}>{t7}{t9}{t10}{t11}</BaseButton>;
    $[19] = t10;
    $[20] = t11;
    $[21] = t2;
    $[22] = t3;
    $[23] = t5;
    $[24] = t7;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}
type FilterChipsProps = {
  activeModal: FilterType | null;
  onClose: () => void;
  onDateClick: () => void;
  onPersonClick: () => void;
  onVariantClick: () => void;
  onPaxSubmit: (updatedFilter: ExperienceFilter) => void;
};
function FilterChips(t0) {
  const $ = _c(50);
  const {
    activeModal,
    onClose,
    onDateClick,
    onPersonClick,
    onVariantClick,
    onPaxSubmit
  } = t0;
  const {
    updateUrlState
  } = useUrlState();
  const {
    filter,
    experience
  } = useExperienceFilterContext();
  const {
    allOptions
  } = useExperienceOptions(experience.variants);
  const isMobile = useMediaState("mobile");
  const {
    availableDates,
    isLoading
  } = useAppExperienceAvailableDates(experience.publicId, filter.selectedVariant, filter.selectedOptions);
  let t1;
  bb0: {
    if (!filter.from) {
      t1 = "";
      break bb0;
    }
    let t2;
    if ($[0] !== filter.from) {
      t2 = formatDateWithoutYear(filter.from);
      $[0] = filter.from;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    const from = t2;
    const to = filter.to ? formatDateWithoutYear(filter.to) : "";
    t1 = to ? `${from} – ${to}` : from;
  }
  const formattedRange = t1;
  let t2;
  if ($[2] !== allOptions || $[3] !== filter.selectedOptions) {
    t2 = {
      allOptions,
      selectedOptions: filter.selectedOptions,
      fallback: "Personen"
    };
    $[2] = allOptions;
    $[3] = filter.selectedOptions;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const paxLabel = useAppExperiencePersonsLabel(t2);
  const hasMultipleVariants = experience.variants?.length > 1;
  let t3;
  if ($[5] !== experience.variants || $[6] !== filter.selectedVariant || $[7] !== hasMultipleVariants) {
    t3 = hasMultipleVariants ? experience.variants.find(v => v.id === filter.selectedVariant)?.title : "";
    $[5] = experience.variants;
    $[6] = filter.selectedVariant;
    $[7] = hasMultipleVariants;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const variantLabel = t3;
  let t4;
  if ($[9] !== onPaxSubmit || $[10] !== updateUrlState) {
    t4 = updatedFilter => {
      updateUrlState({
        ...updatedFilter
      });
      onPaxSubmit(updatedFilter);
    };
    $[9] = onPaxSubmit;
    $[10] = updateUrlState;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handlePaxSubmit = t4;
  if (isLoading) {
    return null;
  }
  let t5;
  if ($[12] !== hasMultipleVariants || $[13] !== onVariantClick || $[14] !== variantLabel) {
    t5 = hasMultipleVariants && <Chip variant="blue" active={true} IconStart={CardsThree} onClick={onVariantClick}>{variantLabel}</Chip>;
    $[12] = hasMultipleVariants;
    $[13] = onVariantClick;
    $[14] = variantLabel;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== onPersonClick || $[17] !== paxLabel) {
    t6 = <Chip variant="blue" active={true} IconStart={User} onClick={onPersonClick}>{paxLabel}</Chip>;
    $[16] = onPersonClick;
    $[17] = paxLabel;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== filter || $[20] !== formattedRange || $[21] !== onDateClick) {
    t7 = <DateChip filter={filter} todayLabel="ab heute" formattedRange={formattedRange} onClick={onDateClick} />;
    $[19] = filter;
    $[20] = formattedRange;
    $[21] = onDateClick;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t5 || $[24] !== t6 || $[25] !== t7) {
    t8 = <div className="flex flex-row flex-wrap gap-2 sm:hidden">{t5}{t6}{t7}</div>;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== activeModal || $[28] !== isMobile || $[29] !== onClose) {
    t9 = !isMobile && <AppVariantModal isOpen={activeModal === FilterType.variant} onClose={onClose} />;
    $[27] = activeModal;
    $[28] = isMobile;
    $[29] = onClose;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== activeModal || $[32] !== isMobile || $[33] !== onClose) {
    t10 = isMobile && <AppMultiVariantModal isOpen={activeModal === FilterType.variant} onClose={onClose} />;
    $[31] = activeModal;
    $[32] = isMobile;
    $[33] = onClose;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const t11 = activeModal === FilterType.person;
  let t12;
  if ($[35] !== handlePaxSubmit || $[36] !== onClose || $[37] !== t11) {
    t12 = <AppPersonModal isOpen={t11} onSubmit={handlePaxSubmit} onClose={onClose} />;
    $[35] = handlePaxSubmit;
    $[36] = onClose;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  const t13 = activeModal === FilterType.date;
  let t14;
  if ($[39] !== availableDates || $[40] !== experience.userTimeslotRequestsAllowed || $[41] !== onClose || $[42] !== t13) {
    t14 = <AppDateModal isOpen={t13} onClose={onClose} availableDates={availableDates} userTimeslotRequestsAllowed={experience.userTimeslotRequestsAllowed} />;
    $[39] = availableDates;
    $[40] = experience.userTimeslotRequestsAllowed;
    $[41] = onClose;
    $[42] = t13;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== t10 || $[45] !== t12 || $[46] !== t14 || $[47] !== t8 || $[48] !== t9) {
    t15 = <>{t8}{t9}{t10}{t12}{t14}</>;
    $[44] = t10;
    $[45] = t12;
    $[46] = t14;
    $[47] = t8;
    $[48] = t9;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  return t15;
}
function PaymentSection() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <li className="flex items-center gap-4"><PayPalIcon bgColor="#F1F0EB" decorative={true} /><span className="font-body-copy">Sicher und flexibel.</span></li>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ul className="flex flex-col gap-3">{t0}<li className="flex items-center gap-4"><KlarnaIcon bgColor="#F1F0EB" decorative={true} /><span className="font-body-copy">Jetzt oder später.</span></li></ul>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
type HeadlineProps = {
  cardSliderListRef: RefObject<HTMLUListElement | null>;
  timeslots: EdpTimeslot[];
};
function Headline(t0) {
  const $ = _c(10);
  const {
    cardSliderListRef,
    timeslots
  } = t0;
  const isMobile = useMediaState("mobile");
  const {
    canScrollLeft,
    canScrollRight,
    checkScrollable
  } = useCanScroll(cardSliderListRef);
  const showNavButtons = (canScrollLeft || canScrollRight) && !isMobile;
  let t1;
  let t2;
  if ($[0] !== checkScrollable) {
    t1 = () => {
      checkScrollable();
    };
    t2 = [checkScrollable];
    $[0] = checkScrollable;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h2 className="font-heading2">Termine</h2>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== cardSliderListRef || $[5] !== showNavButtons || $[6] !== timeslots) {
    t4 = showNavButtons && <CardSliderNavButtons sliderListRef={cardSliderListRef} amountOfItems={timeslots.length} ariaLabelPrevious="Vorheriger Termin" ariaLabelNext={"N\xE4chster Termin"} />;
    $[4] = cardSliderListRef;
    $[5] = showNavButtons;
    $[6] = timeslots;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <div className="pointer-events-auto flex items-center justify-between">{t3}{t4}</div>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
type AddToCartModalProps = {
  timeslot: EdpTimeslot | null;
  pricesUnavailable?: boolean;
  onClose: () => void;
  onSubmit: (timeslot: EdpTimeslot) => Promise<boolean>;
};
function AddToCartModal(t0) {
  const $ = _c(44);
  const {
    timeslot,
    pricesUnavailable: t1,
    onClose,
    onSubmit
  } = t0;
  const pricesUnavailable = t1 === undefined ? false : t1;
  const {
    filter,
    experience
  } = useExperienceFilterContext();
  const isCurrencyMismatch = useIsCartCurrencyMismatch(experience.currency);
  const {
    allOptions
  } = useExperienceOptions(experience.variants);
  const hasMultipleVariants = experience.variants?.length > 1;
  let t2;
  if ($[0] !== experience.variants || $[1] !== filter.selectedVariant || $[2] !== hasMultipleVariants) {
    t2 = hasMultipleVariants ? experience.variants.find(v => v.id === filter.selectedVariant)?.title : "";
    $[0] = experience.variants;
    $[1] = filter.selectedVariant;
    $[2] = hasMultipleVariants;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const variantLabel = t2;
  let t3;
  if ($[4] !== allOptions || $[5] !== filter.selectedOptions) {
    t3 = {
      allOptions,
      selectedOptions: filter.selectedOptions,
      fallback: "Personen"
    };
    $[4] = allOptions;
    $[5] = filter.selectedOptions;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const paxLabel = useAppExperiencePersonsLabel(t3);
  if (!timeslot) {
    return null;
  }
  const t4 = !!timeslot;
  let t5;
  if ($[7] !== onClose) {
    t5 = <Modal.Header onClose={onClose}>Auswahl prüfen</Modal.Header>;
    $[7] = onClose;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== timeslot.date) {
    t6 = timeslot.date && <ListItem renderIcon={_temp3} interactive={false}>{formatDateWithWeekday(timeslot.date)}</ListItem>;
    $[9] = timeslot.date;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== timeslot.duration || $[12] !== timeslot.startTime) {
    t7 = timeslot.startTime && <ListItem renderIcon={_temp4} interactive={false}>{`${timeslot.startTime} Uhr · ${timeslot.duration / 60} Stunden`}</ListItem>;
    $[11] = timeslot.duration;
    $[12] = timeslot.startTime;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== variantLabel) {
    t8 = variantLabel && <ListItem renderIcon={_temp5} interactive={false}>{variantLabel}</ListItem>;
    $[14] = variantLabel;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== paxLabel) {
    t9 = <ListItem renderIcon={_temp6} interactive={false}>{paxLabel}</ListItem>;
    $[16] = paxLabel;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t6 || $[19] !== t7 || $[20] !== t8 || $[21] !== t9) {
    t10 = <Modal.Content><List>{t6}{t7}{t8}{t9}</List></Modal.Content>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== experience.currency || $[24] !== timeslot.price) {
    t11 = <Price amount={timeslot.price} currency={experience.currency} className="font-body-copy font-semibold" />;
    $[23] = experience.currency;
    $[24] = timeslot.price;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span>inkl. MwSt.</span>;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t11) {
    t13 = <Modal.Footer.InfoBox><div className="flex flex-col">{t11}{t12}</div></Modal.Footer.InfoBox>;
    $[27] = t11;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== onSubmit || $[30] !== timeslot) {
    t14 = () => onSubmit(timeslot);
    $[29] = onSubmit;
    $[30] = timeslot;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const t15 = !timeslot || isCurrencyMismatch || pricesUnavailable;
  let t16;
  if ($[32] !== t14 || $[33] !== t15) {
    t16 = <Modal.Footer.Actions><Modal.Footer.Button onClick={t14} disabled={t15}>In den Warenkorb</Modal.Footer.Button></Modal.Footer.Actions>;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t13 || $[36] !== t16) {
    t17 = <Modal.Footer>{t13}{t16}</Modal.Footer>;
    $[35] = t13;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== onClose || $[39] !== t10 || $[40] !== t17 || $[41] !== t4 || $[42] !== t5) {
    t18 = <Modal isOpen={t4} onClose={onClose}>{t5}{t10}{t17}</Modal>;
    $[38] = onClose;
    $[39] = t10;
    $[40] = t17;
    $[41] = t4;
    $[42] = t5;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  return t18;
}
function _temp6() {
  return <User size={24} />;
}
function _temp5() {
  return <CardsThree size={24} />;
}
function _temp4() {
  return <Clock size={24} />;
}
function _temp3() {
  return <CalendarBlank size={24} />;
}